import { useLocation, Link, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Sparkles, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { useSEO } from "../hooks/useSEO";
import { useJsonLd, personSchema, breadcrumb } from "../hooks/useJsonLd";
import { consultantPages } from "../data/consultantPages";

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-100px" }, transition: { duration: 0.6 } };

export default function Consultant() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "").replace(/\/$/, "");
  const data = consultantPages[slug];

  useSEO({
    title: data?.seoTitle || "Consultant · Shivani Ranawat",
    description: data?.seoDesc || "",
    path: `/${slug}`,
  });
  useJsonLd(
    data
      ? [
          personSchema,
          breadcrumb([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: data.h1, path: `/${slug}` },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: data.h1,
            description: data.seoDesc,
            provider: { "@id": "https://marketing-ops-18.emergent.host/#shivani" },
            areaServed: "Worldwide",
            url: `https://marketing-ops-18.emergent.host/${slug}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: data.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]
      : []
  );

  if (!data) return <Navigate to="/services" replace />;

  return (
    <div data-testid={`consultant-${slug}`}>
      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pb-24 bg-[#0F1115] text-white mesh-bg grain overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 relative">
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-white/50">{data.eyebrow}</div>
          <motion.h1 {...fadeUp} className="mt-6 font-display text-4xl sm:text-5xl md:text-7xl leading-[1.02] tracking-tight max-w-4xl">
            {data.h1}<span className="text-[#3B82F6]">.</span>
          </motion.h1>
          <p className="mt-6 text-white/70 text-base md:text-lg max-w-3xl leading-relaxed">{data.lead}</p>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-white text-neutral-900 px-5 py-3 text-sm font-medium hover:bg-white/90">
              Book a strategy call <ArrowRight size={14} />
            </Link>
            <Link to="/work" className="inline-flex items-center gap-2 rounded-full border border-white/25 text-white px-5 py-3 text-sm font-medium hover:border-white/60">
              See case studies
            </Link>
          </div>
        </div>
      </section>

      {/* Problems */}
      <section className="bg-[#F7F7F5] py-20 md:py-28 border-t border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500">01 — Problems solved</div>
              <h2 className="mt-4 font-display text-3xl md:text-5xl text-neutral-900 tracking-tight leading-[1.05]">
                If any of this sounds familiar,<br /><span className="text-neutral-400">we should talk.</span>
              </h2>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.problems.map((p, i) => (
                <div key={p} className="rounded-2xl border border-neutral-200 bg-white p-5 hover:border-[#3B82F6] transition">
                  <div className="text-[10px] font-mono text-neutral-400">{String(i + 1).padStart(2, "0")}</div>
                  <div className="mt-4 font-display text-lg text-neutral-900">{p}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Approach */}
      <section className="bg-white py-20 md:py-28 border-t border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
            <div className="lg:col-span-4 text-xs font-mono uppercase tracking-[0.2em] text-neutral-500">02 — Approach</div>
            <div className="lg:col-span-8">
              <h2 className="font-display text-3xl md:text-5xl text-neutral-900 tracking-tight leading-[1.05]">
                A repeatable sequence,<br /><span className="text-neutral-400">not a magic playbook.</span>
              </h2>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.approach.map((a, i) => (
              <div key={a.t} className="rounded-2xl border border-neutral-200 bg-neutral-50 p-6 hover:border-[#3B82F6] hover:bg-white transition">
                <div className="text-[10px] font-mono text-[#3B82F6]">{String(i + 1).padStart(2, "0")}</div>
                <div className="mt-6 font-display text-xl text-neutral-900">{a.t}</div>
                <div className="mt-2 text-sm text-neutral-500">{a.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="bg-[#0F1115] text-white py-20 md:py-28 border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
            <div className="lg:col-span-4 text-xs font-mono uppercase tracking-[0.2em] text-white/50">03 — Expected outcomes</div>
            <div className="lg:col-span-8">
              <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[1.05]">
                What you can expect<br /><span className="text-white/50">in the first 90 days.</span>
              </h2>
            </div>
          </motion.div>

          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.outcomes.map((o) => (
              <li key={o} className="glass-dark rounded-2xl p-5 flex items-start gap-3">
                <CheckCircle2 size={18} className="text-[#10B981] mt-1 shrink-0" />
                <span className="text-base">{o}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20 md:py-28 border-t border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500">FAQ</div>
              <h2 className="mt-4 font-display text-3xl md:text-5xl text-neutral-900 tracking-tight leading-[1.05]">
                Frequently asked.
              </h2>
              <Link to="/contact" className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#3B82F6] hover:text-[#2563EB]">
                Ask a new one <ArrowRight size={14} />
              </Link>
            </div>
            <div className="lg:col-span-8 space-y-3">
              {data.faqs.map((f) => (
                <details key={f.q} className="group rounded-2xl border border-neutral-200 p-5 hover:border-[#3B82F6] transition">
                  <summary className="cursor-pointer font-display text-lg text-neutral-900 flex items-center justify-between gap-4">
                    <span>{f.q}</span>
                    <ChevronRight size={16} className="text-neutral-400 group-open:rotate-90 transition shrink-0" />
                  </summary>
                  <p className="mt-3 text-neutral-600 text-sm leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#0F1115] text-white py-16 md:py-20 border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/40 inline-flex items-center gap-2">
              <Sparkles size={12} className="text-[#3B82F6]" /> Ready to compound?
            </div>
            <div className="mt-2 font-display text-2xl md:text-3xl">Let&apos;s find your revenue leak.</div>
          </div>
          <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-white text-neutral-900 px-5 py-3 text-sm font-medium">
            Book a 30-min audit call <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
